import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, notFoundIdHandler, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  ICodigoOtp,
  IDataCodigoOtp,
  MutationParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchCodigoOTPs = (params?: GetCodigoOTPsParams) => {
  return useQuery({
    queryKey: ['codigo-ot-ps', ...Object.values(params || {})],
    queryFn: () => getCodigoOTPs(params),
  });
};

export const useGetCodigoOTP = (id: string) => {
  return useQuery({
    queryKey: ['codigo-ot-p', id],
    queryFn: () => getCodigoOTP(id),
  });
};

export const useCreateCodigoOTP = ({
  navigate,
  returnUrl,
  returnErrorUrl,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCodigoOTP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codigo-ot-ps'] });
      navigate(returnUrl);
    },
    onError: () => {
      navigate(returnErrorUrl || returnUrl);
    },
  });
};

export const useUpdateCodigoOTP = ({
  navigate,
  returnUrl,
  returnErrorUrl,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCodigoOTP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codigo-ot-ps'] });
      navigate(returnUrl);
    },
    onError: () => {
      navigate(returnErrorUrl || returnUrl);
    },
  });
};

export const useDeleteCodigoOTP = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCodigoOTP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['codigo-ot-ps'] });
    },
    onError: () => {},
  });
};

///* axios
export type GetCodigoOTPsParams = {
  page?: number;
  page_size?: number;

  // filters
  codigo?: string;
  telefono?: string;
  estado_recibido?: boolean;
  enviado?: boolean;
  estado?: string;
  numero_referencia?: string; // prospecto

  prospecto?: number; // fk
};
export type CreateCodigoOTPParams = Omit<IDataCodigoOtp, 'id_codigo_otp'>;
export type UpdateCodigoOTPParams = {
  id: string;
  data: CreateCodigoOTPParams;
};

export const getCodigoOTPs = (params?: GetCodigoOTPsParams) => {
  const queryParams = getUrlParamsFetch(params || {});
  return get<ICodigoOtp>(
    `${urlBackend}api/v1/codigo-otp/?${queryParams}`,
    true
  );
};

export const getCodigoOTP = async (id: string) => {
  const res = await get<IDataCodigoOtp>(
    `${urlBackend}api/v1/codigo-otp/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createCodigoOTP = async (data: CreateCodigoOTPParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await post<IDataCodigoOtp>(
    `${urlBackend}api/v1/codigo-otp/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateCodigoOTP = async ({ id, data }: UpdateCodigoOTPParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await put<IDataCodigoOtp>(
    `${urlBackend}api/v1/codigo-otp/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteCodigoOTP = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<IDataCodigoOtp>(
    `${urlBackend}api/v1/codigo-otp/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
